'use client'
import { useState, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import { usePathname } from 'next/navigation'
import { commercialQuoteHref, filmQuoteHref } from '@/lib/commercial/routes'
import { BUSINESS } from '@/lib/constants'
import BookingTrigger from '@/components/BookingTrigger'
import { TrackedLink } from '@/components/analytics/TrackedLink'
import { SERVICE_REGIONS, type AreaRegion } from '@/lib/data/service-areas'
import { getNeighbourhoodHref } from '@/lib/data/service-area-routes'

const BookingModal = dynamic(() => import(/* webpackChunkName: "booking-modal" */ './BookingModal'), { ssr: false })

const DROPDOWN_ITEM_LIMIT = 6
const DROPDOWN_GRID_COLS = 6
const DROPDOWN_COLS_PER_ROW = 3

type DropdownEntry = { name: string; href: string; live: boolean; nested?: boolean }

function buildRegionEntries(region: AreaRegion): DropdownEntry[] {
  if (region.cities.length === 1) {
    const city = region.cities[0]
    const entries: DropdownEntry[] = [{ name: city.name, href: city.href, live: city.live }]
    const neighbourhoods = city.neighbourhoods
      .filter((n) => getNeighbourhoodHref(city.slug, n.slug))
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
    for (const n of neighbourhoods) {
      entries.push({
        name: n.name,
        href: getNeighbourhoodHref(city.slug, n.slug)!,
        live: false,
        nested: true,
      })
    }
    return entries
  }
  return region.cities.map((c) => ({ name: c.name, href: c.href, live: c.live }))
}

const phoneRaw = BUSINESS.phone.replace(/\D/g, '')

/* ---------- Inline SVG icons (match shell.jsx exactly) ---------- */
function IconMaple(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
      <path d="M12 2 L13 6 L16 4 L15 8 L19 7 L17 10 L21 11 L17 13 L20 16 L16 16 L17 20 L13 17 L12 22 L11 17 L7 20 L8 16 L4 16 L7 13 L3 11 L7 10 L5 7 L9 8 L8 4 L11 6 Z" />
    </svg>
  )
}
function IconPhone(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="square" {...p}>
      <path d="M5 3 H9 L11 8 L8.5 10 C9.5 13 11 14.5 14 15.5 L16 13 L21 15 V19 C21 20 20 21 19 21 C10 21 3 14 3 5 C3 4 4 3 5 3 Z" />
    </svg>
  )
}
function IconInstagram(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="square" {...p}>
      <rect x="3" y="3" width="18" height="18" rx="4" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.8" fill="currentColor" />
    </svg>
  )
}
function IconArrowRight(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" {...p}>
      <path d="M5 12 H19 M13 6 L19 12 L13 18" />
    </svg>
  )
}
function IconMenu(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="square" {...p}>
      <path d="M4 6 H20 M4 12 H20 M4 18 H20" />
    </svg>
  )
}
function IconClose(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="square" {...p}>
      <path d="M5 5 L19 19 M19 5 L5 19" />
    </svg>
  )
}

/* ---------- Nav links ---------- */
// Sitewide nav links carry the most internal-link weight on the site, which is
// what Google reads when choosing SERP sitelinks. Keep this list to the pages
// that should be eligible: services, service areas, and the two trust/contact
// endpoints. Adding deep pages here dilutes the signal.
//
// The service pages sit under a "Services" dropdown rather than as
// top-level items. Seven top-level items plus the wordmark plus the CTA needed
// 1240px inside a 1216px container, so every multi-word label silently wrapped
// to two lines at *every* desktop width. Folding them frees ~200px.
//
// The dropdown panel is always in the DOM and hidden with CSS (opacity /
// visibility), never conditionally mounted, so the service links stay in the
// sitewide markup and keep the link weight the note above is about.
const SERVICE_LINKS = [
  { label: 'Security Film', href: '/products/window-film', blurb: 'Security film for vulnerable glass' },
  { label: 'Door Fortification', href: '/products/door-fortification', blurb: 'Frame and strike-zone reinforcement' },
  { label: 'Solar Film', href: '/products/solar-film', blurb: 'Heat & glare control for homes & businesses' },
  { label: 'Privacy Film', href: '/products/privacy-film', blurb: 'Privacy for homes, condos & businesses' },
  { label: 'Commercial', href: '/commercial', blurb: 'Solar, security and privacy film' },
  { label: 'Film Comparisons', href: '/window-film-comparisons', blurb: 'Compare security, solar and privacy film' },
]

// Discriminated so the `l.children ? ... : ...` branches below narrow `href`
// to a plain string in the link branches.
type NavLink =
  | { label: string; children: typeof SERVICE_LINKS; href?: undefined; hasDropdown?: undefined }
  | { label: string; href: string; hasDropdown?: boolean; children?: undefined }

const NAV_LINKS: NavLink[] = [
  { label: 'Services', children: SERVICE_LINKS },
  { label: 'Service Areas', href: '/service-areas', hasDropdown: true },
  { label: 'Learning Hub', href: '/blog' },
  { label: 'About', href: '/about' },
  { label: 'Contact', href: '/contact' },
]

/* ---------- TopBar ---------- */
function TopBar() {
  return (
    <div
      className="topbar"
      style={{
        background: 'var(--navy-950)',
        color: 'rgba(255,255,255,0.75)',
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <div
        className="topbar-inner"
        style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '8px 32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 24,
        }}
      >
        <div className="topbar-left" style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <IconMaple width={14} height={14} style={{ color: 'var(--maple)' }} /> Canadian owned &amp; operated
          </span>
          <span className="topbar-sep" style={{ opacity: 0.4 }}>·</span>
          <span className="topbar-xpel">XPEL certified installer</span>
          <span className="topbar-sep" style={{ opacity: 0.4 }}>·</span>
          <span className="topbar-area">Toronto &amp; the GTA</span>
        </div>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
          <a
            href="https://instagram.com/ClearGuardGTA"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Clear Guard on Instagram"
            style={{ color: 'inherit', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
          >
            <IconInstagram width={14} height={14} />
          </a>
          <span className="topbar-sep" style={{ opacity: 0.4 }}>·</span>
          <TrackedLink
            href={`tel:${phoneRaw}`}
            location="header"
            label="topbar_call_now"
            style={{ color: 'inherit', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <IconPhone width={11} height={11} /> Call Now
          </TrackedLink>
        </div>
      </div>
    </div>
  )
}

/* ---------- NavBar ---------- */
function NavBar() {
  const pathname = usePathname()
  const productQuoteHref = filmQuoteHref(pathname)
  const quoteHref = productQuoteHref ?? commercialQuoteHref(pathname)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [areasOpen, setAreasOpen] = useState(false)
  const [servicesOpen, setServicesOpen] = useState(false)
  const [mobileAreasOpen, setMobileAreasOpen] = useState(false)
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false)
  const [hidden, setHidden] = useState(false)
  const [bookingNonce, setBookingNonce] = useState(0)

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [mobileOpen])

  useEffect(() => {
    // Auto-hide on scroll is mobile-only. Desktop keeps the nav pinned.
    const mq = window.matchMedia('(max-width: 768px)')

    let lastY = window.scrollY
    let ticking = false
    const onScroll = () => {
      if (!mq.matches) {
        if (hidden) setHidden(false)
        return
      }
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        const y = window.scrollY
        const delta = y - lastY
        if (y < 80) setHidden(false)
        else if (delta > 4) setHidden(true)
        else if (delta < -4) setHidden(false)
        lastY = y
        ticking = false
      })
    }
    const onMqChange = () => {
      if (!mq.matches) setHidden(false)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    mq.addEventListener('change', onMqChange)
    return () => {
      window.removeEventListener('scroll', onScroll)
      mq.removeEventListener('change', onMqChange)
    }
  }, [hidden])

  return (
    <>
    <nav
      style={{
        background: '#fff',
        borderBottom: '1px solid var(--line)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
        transform: hidden && !mobileOpen ? 'translateY(-100%)' : 'translateY(0)',
        transition: 'transform 0.25s ease',
      }}
    >
      <div
        className="nav-inner"
        style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '14px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 32,
        }}
      >
        {/* Logo */}
        <Link href="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <Image
            src="/assets/logo-shield.png"
            alt="Clear Guard"
            width={36}
            height={36}
            priority
            style={{ height: 36, width: 'auto' }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
            <span
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 800,
                fontSize: 18,
                letterSpacing: '0.06em',
                color: 'var(--navy-700)',
              }}
            >
              CLEAR GUARD
            </span>
            <span
              className="nav-tagline"
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 9,
                letterSpacing: '0.22em',
                color: 'var(--muted)',
                marginTop: 3,
              }}
            >
              HOME FORTIFICATION
            </span>
          </div>
        </Link>

        {/* Desktop nav links */}
        <div className="nav-links" style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
          {NAV_LINKS.map((l) =>
            l.children ? (
              <div
                key={l.label}
                className="nav-dropdown"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
                // Keyboard users never fire mouseenter. Driving open state from
                // focus as well as hover keeps the panel reachable by Tab; the
                // :focus-within rule in responsive.css is the no-JS fallback.
                onFocus={() => setServicesOpen(true)}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setServicesOpen(false)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape' && servicesOpen) {
                    setServicesOpen(false)
                    e.currentTarget.querySelector('button')?.focus()
                  }
                }}
                style={{ position: 'relative' }}
              >
                {/* No /services index page exists, so the trigger is a button
                    rather than a link to nowhere. The service pages are the
                    links. */}
                <button
                  type="button"
                  onClick={() => setServicesOpen((v) => !v)}
                  aria-expanded={servicesOpen}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    color: 'var(--ink)',
                    fontSize: 14,
                    fontWeight: 500,
                    fontFamily: 'var(--font-body)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {l.label}
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 10 10"
                    aria-hidden="true"
                    style={{
                      transform: servicesOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.18s',
                    }}
                  >
                    <path d="M 2 3.5 L 5 6.5 L 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </button>
                <div
                  className="nav-dropdown-panel"
                  data-open={servicesOpen ? 'true' : 'false'}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    paddingTop: 14,
                    zIndex: 100,
                  }}
                >
                  <div
                    style={{
                      background: '#fff',
                      border: '1px solid var(--line)',
                      boxShadow: '0 24px 48px -12px rgba(8,25,42,0.18)',
                      minWidth: 268,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {l.children.map((c, i) => (
                      <Link
                        key={c.href}
                        href={c.href}
                        className="nav-dropdown-item"
                        style={{
                          display: 'block',
                          padding: '14px 20px',
                          textDecoration: 'none',
                          borderTop: i === 0 ? 'none' : '1px solid var(--line)',
                        }}
                      >
                        <span
                          style={{
                            display: 'block',
                            fontFamily: 'var(--font-display)',
                            fontWeight: 600,
                            fontSize: 14,
                            color: 'var(--ink)',
                          }}
                        >
                          {c.label}
                        </span>
                        <span
                          style={{
                            display: 'block',
                            marginTop: 4,
                            fontSize: 12,
                            fontFamily: 'var(--font-body)',
                            color: 'var(--muted)',
                          }}
                        >
                          {c.blurb}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : l.hasDropdown ? (
              <div
                key={l.label}
                onMouseEnter={() => setAreasOpen(true)}
                onMouseLeave={() => setAreasOpen(false)}
                style={{ position: 'relative' }}
              >
                <Link
                  href={l.href}
                  style={{
                    textDecoration: 'none',
                    color: 'var(--ink)',
                    fontSize: 14,
                    fontWeight: 500,
                    fontFamily: 'var(--font-body)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {l.label}
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 10 10"
                    style={{
                      transform: areasOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.18s',
                    }}
                  >
                    <path d="M 2 3.5 L 5 6.5 L 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </Link>
                {areasOpen && (
                  <div
                    className="nav-areas-panel"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      paddingTop: 14,
                      width: 'min(820px, 92vw)',
                      zIndex: 100,
                    }}
                  >
                  <div
                    style={{
                      background: '#fff',
                      border: '1px solid var(--line)',
                      boxShadow: '0 24px 48px -12px rgba(8,25,42,0.18)',
                      display: 'grid',
                      gridTemplateColumns: `repeat(${DROPDOWN_GRID_COLS}, 1fr)`,
                    }}
                  >
                    {(() => {
                      const total = SERVICE_REGIONS.length
                      const fullSpan = DROPDOWN_GRID_COLS / DROPDOWN_COLS_PER_ROW
                      const remainder = total % DROPDOWN_COLS_PER_ROW
                      const lastRowCount = remainder === 0 ? DROPDOWN_COLS_PER_ROW : remainder
                      const lastRowStart = total - lastRowCount
                      const lastSpan = DROPDOWN_GRID_COLS / lastRowCount
                      return SERVICE_REGIONS.map((region, ri) => {
                        const inLastRow = ri >= lastRowStart
                        const span = inLastRow ? lastSpan : fullSpan
                        const itemsInRow = inLastRow ? lastRowCount : DROPDOWN_COLS_PER_ROW
                        const positionInRow = inLastRow ? ri - lastRowStart : ri % DROPDOWN_COLS_PER_ROW
                        const isLastInRow = positionInRow === itemsInRow - 1
                        const entries = buildRegionEntries(region)
                        const visible = entries.slice(0, DROPDOWN_ITEM_LIMIT)
                        const overflow = entries.length - visible.length
                        return (
                          <div
                            key={region.slug}
                            style={{
                              gridColumn: `span ${span}`,
                              padding: '20px 22px',
                              borderRight: !isLastInRow ? '1px solid var(--line)' : 'none',
                              borderBottom: !inLastRow ? '1px solid var(--line)' : 'none',
                            }}
                          >
                            <div
                              className="mono"
                              style={{
                                fontSize: 10,
                                letterSpacing: '0.18em',
                                color: 'var(--accent-hot)',
                                textTransform: 'uppercase',
                                marginBottom: 12,
                              }}
                            >
                              {region.region}
                            </div>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {visible.map((entry) => (
                                <li key={entry.name}>
                                  <Link
                                    href={entry.href}
                                    style={{
                                      color: entry.nested ? 'var(--muted)' : 'var(--ink)',
                                      textDecoration: 'none',
                                      fontSize: 13,
                                      fontFamily: 'var(--font-body)',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 6,
                                      opacity: entry.nested || entry.live ? 1 : 0.6,
                                      paddingLeft: entry.nested ? 10 : 0,
                                    }}
                                  >
                                    {entry.name}
                                    {entry.live && (
                                      <span style={{ width: 5, height: 5, background: 'var(--ok)', borderRadius: '50%', flexShrink: 0 }} />
                                    )}
                                  </Link>
                                </li>
                              ))}
                              {overflow > 0 && (
                                <li>
                                  <Link
                                    href={region.cities.length === 1 ? region.cities[0].href : '/service-areas'}
                                    className="mono"
                                    style={{
                                      fontSize: 10,
                                      letterSpacing: '0.16em',
                                      color: 'var(--muted)',
                                      textDecoration: 'none',
                                      textTransform: 'uppercase',
                                    }}
                                  >
                                    + {overflow} more →
                                  </Link>
                                </li>
                              )}
                            </ul>
                          </div>
                        )
                      })
                    })()}

                    {/* Footer row */}
                    <div
                      style={{
                        gridColumn: '1 / -1',
                        padding: '14px 22px',
                        borderTop: '1px solid var(--line)',
                        background: 'var(--paper)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        className="mono"
                        style={{ fontSize: 10, letterSpacing: '0.16em', color: 'var(--muted)', textTransform: 'uppercase' }}
                      >
                        Don&rsquo;t see your city? We likely still serve it.
                      </span>
                      <Link
                        href="/service-areas"
                        style={{
                          fontFamily: 'var(--font-display)',
                          fontWeight: 600,
                          fontSize: 13,
                          color: 'var(--accent-hot)',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        All service areas <IconArrowRight width={12} height={12} />
                      </Link>
                    </div>
                  </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={l.label}
                href={l.href}
                style={{
                  textDecoration: 'none',
                  color: 'var(--ink)',
                  fontSize: 14,
                  fontWeight: 500,
                  fontFamily: 'var(--font-body)',
                }}
              >
                {l.label}
              </Link>
            )
          )}
        </div>

        {/* Desktop CTA */}
        {quoteHref ? (
          <Link href={quoteHref} className="btn btn-primary nav-cta">
            <span className="cta-label">{productQuoteHref ? 'Request a Film Quote' : 'Request Commercial Quote'}</span>
            <IconArrowRight width={14} height={14} />
          </Link>
        ) : <BookingTrigger ctaLocation="header">
          <button className="btn btn-primary nav-cta">
            <span className="cta-label">Book Free Assessment</span>
            <IconArrowRight width={14} height={14} />
          </button>
        </BookingTrigger>}

        {/* Hamburger */}
        <button
          className="nav-hamburger"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          aria-expanded={mobileOpen}
          aria-controls="mobile-drawer"
          style={{
            display: 'none',
            background: 'transparent',
            border: '1px solid var(--line-strong)',
            width: 44,
            height: 44,
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'var(--ink)',
          }}
        >
          <IconMenu width={22} height={22} />
        </button>
      </div>
    </nav>

    {/* Mobile drawer — rendered outside nav so the nav's transform doesn't break position:fixed */}
    {mobileOpen && (
      <div
        onClick={() => setMobileOpen(false)}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(8,25,42,0.7)',
          zIndex: 80,
          backdropFilter: 'blur(4px)',
        }}
      >
          <div
            id="mobile-drawer"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              width: 'min(320px, 85vw)',
              background: '#fff',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '18px 20px',
                borderBottom: '1px solid var(--line)',
              }}
            >
              <span className="display" style={{ fontSize: 16, letterSpacing: '0.06em', color: 'var(--navy-700)' }}>
                MENU
              </span>
              <button
                onClick={() => setMobileOpen(false)}
                aria-label="Close"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconClose width={18} height={18} />
              </button>
            </div>
            <nav style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
              {NAV_LINKS.map((l) =>
                l.children ? (
                  <div key={l.label} style={{ borderBottom: '1px solid var(--line)' }}>
                    <button
                      onClick={() => setMobileServicesOpen((v) => !v)}
                      aria-expanded={mobileServicesOpen}
                      style={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px 24px',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: 'var(--font-display)',
                        fontWeight: 600,
                        fontSize: 16,
                        color: 'var(--ink)',
                        textAlign: 'left',
                      }}
                    >
                      <span>{l.label}</span>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 10 10"
                        aria-hidden="true"
                        style={{
                          transform: mobileServicesOpen ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.18s',
                        }}
                      >
                        <path d="M 2 3.5 L 5 6.5 L 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                      </svg>
                    </button>
                    {mobileServicesOpen && (
                      <div style={{ padding: '4px 0 12px', background: 'var(--paper)' }}>
                        {l.children.map((c) => (
                          <Link
                            key={c.href}
                            href={c.href}
                            onClick={() => setMobileOpen(false)}
                            style={{
                              display: 'block',
                              padding: '12px 24px 12px 36px',
                              fontFamily: 'var(--font-body)',
                              fontSize: 15,
                              color: 'var(--ink)',
                              textDecoration: 'none',
                            }}
                          >
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : l.hasDropdown ? (
                  <div key={l.label} style={{ borderBottom: '1px solid var(--line)' }}>
                    <button
                      onClick={() => setMobileAreasOpen((v) => !v)}
                      aria-expanded={mobileAreasOpen}
                      style={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px 24px',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: 'var(--font-display)',
                        fontWeight: 600,
                        fontSize: 16,
                        color: 'var(--ink)',
                        textAlign: 'left',
                      }}
                    >
                      <span>{l.label}</span>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 10 10"
                        style={{
                          transform: mobileAreasOpen ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.18s',
                        }}
                      >
                        <path d="M 2 3.5 L 5 6.5 L 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                      </svg>
                    </button>
                    {mobileAreasOpen && (
                      <div style={{ padding: '0 24px 16px', background: 'var(--paper)' }}>
                        <Link
                          href="/service-areas"
                          onClick={() => setMobileOpen(false)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            marginTop: 12,
                            fontFamily: 'var(--font-display)',
                            fontWeight: 600,
                            fontSize: 13,
                            color: 'var(--accent-hot)',
                            textDecoration: 'none',
                          }}
                        >
                          All service areas <IconArrowRight width={12} height={12} />
                        </Link>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={l.label}
                    href={l.href}
                    onClick={() => setMobileOpen(false)}
                    style={{
                      display: 'block',
                      padding: '16px 24px',
                      fontFamily: 'var(--font-display)',
                      fontWeight: 600,
                      fontSize: 16,
                      color: 'var(--ink)',
                      textDecoration: 'none',
                      borderBottom: '1px solid var(--line)',
                    }}
                  >
                    {l.label}
                  </Link>
                )
              )}
            </nav>
            <div
              style={{
                padding: 20,
                borderTop: '1px solid var(--line)',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              {quoteHref ? (
                <Link href={quoteHref} className="btn btn-primary" onClick={() => setMobileOpen(false)} style={{ justifyContent: 'center', width: '100%' }}>
                  Get a Quote <IconArrowRight width={14} height={14} />
                </Link>
              ) : <button
                className="btn btn-primary"
                onClick={() => {
                  setBookingNonce((n) => n + 1)
                  setMobileOpen(false)
                }}
                style={{ justifyContent: 'center', width: '100%' }}
              >
                Book Free Assessment <IconArrowRight width={14} height={14} />
              </button>}
              <TrackedLink
                href={`tel:${phoneRaw}`}
                location="header"
                label="mobile_drawer_call"
                className="btn btn-ghost"
                style={{ justifyContent: 'center', textDecoration: 'none' }}
              >
                <IconPhone width={14} height={14} /> {BUSINESS.phone}
              </TrackedLink>
            </div>
          </div>
        </div>
      )}

      {/* Booking modal mount, lifted above the mobile drawer so closing the
          drawer doesn't unmount the modal mid-open. Nonce-keyed so a second
          click always remounts with initialOpen=true. */}
      {!quoteHref && bookingNonce > 0 && (
        <BookingModal
          key={bookingNonce}
          initialOpen
          trigger={<span style={{ display: 'none' }} />}
        />
      )}
    </>
  )
}

/* ---------- Header (TopBar + NavBar) ---------- */
export default function Header() {
  return (
    <>
      <TopBar />
      <NavBar />
    </>
  )
}
